import {
  Component,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { HeaderComponent } from '../../header/header.component';
import { FooterComponent } from '../../footer/footer.component';

@Component({
  selector: 'app-terms-of-service',
  standalone: true,
  imports: [CommonModule, RouterModule, HeaderComponent, FooterComponent],
  templateUrl: './terms-of-service.component.html',
  styleUrls: ['./terms-of-service.component.css'],
})
export class TermsOfServiceComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('policyContent') policyContent!: ElementRef;

  lastUpdated: string = 'October 30, 2025';
  activeSection: string = '';
  private observer!: IntersectionObserver;
  private isScrolling = false;

  sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: `
      <p>Welcome to <strong>Markado</strong>! These Terms of Service (“Terms”) explain the basic rules for using our marketplace. By creating an account or using Markado, you agree to these Terms.</p>
      <p>Markado is operated by two independent founders based in Tetovo, North Macedonia. We built this platform to connect local buyers and sellers in a simple, transparent, and safe way.</p>
      <p>If you have any questions, write us at our contact page.</p>
    `,
    },
    {
      id: 'eligibility',
      title: 'Who Can Use Markado',
      content: `
      <p>You must be at least 18 years old to use Markado. Creating an account means you confirm that you're legally able to buy and sell.</p>
      <p>When registering, please provide accurate information (real name, valid contact details) and keep it updated. You're responsible for all activity under your account and for keeping your password secure.</p>
      <p>Each user may have only one active account. Markado primarily serves users in North Macedonia and nearby regions.</p>
    `,
    },
    {
      id: 'marketplace-use',
      title: 'Using the Marketplace',
      content: `
      <p><strong>Our Role:</strong> Markado provides the platform - we don't own, sell, or guarantee the items listed. Buyers and sellers communicate and transact directly.</p>
      <p><strong>Sellers must:</strong></p>
      <ul>
        <li>Only list legal and accurately described items</li>
        <li>Use real photos of actual products</li>
        <li>Complete transactions honestly and fairly</li>
      </ul>
      <p><strong>Buyers must:</strong></p>
      <ul>
        <li>Make genuine inquiries and prompt payments</li>
        <li>Communicate respectfully and arrange delivery responsibly</li>
      </ul>
      <p>Illegal, counterfeit, or unsafe items, adult content, and anything prohibited by law are strictly banned.</p>
    `,
    },
    {
      id: 'fees-payments',
      title: 'Fees and Payments',
      content: `
      <p>Most features are free. Optional paid features (like premium listings) may require topping up your account balance. Payments are processed securely by third-party providers; Markado does not store card details.</p>
      <p>Any fees or top-ups will always be clearly shown before confirmation. Refunds for items are handled by the sellers directly.</p>
    `,
    },
    {
      id: 'content',
      title: 'Your Content and Conduct',
      content: `
      <p>You retain ownership of content you upload (listings, photos, descriptions), but grant Markado a license to display it as part of our service.</p>
      <p>Content must be accurate, legal, and respectful. We may remove listings or suspend accounts that violate laws or community standards.</p>
      <p>Be respectful in messages and avoid spam, harassment, or false information. Repeated violations can lead to suspension or permanent bans.</p>
    `,
    },
    {
      id: 'liability',
      title: 'Limitations and Responsibilities',
      content: `
      <p>Markado provides a platform “as is.” We can't guarantee the quality, legality, or accuracy of listings or the conduct of other users.</p>
      <p>We strive to keep the site running reliably but can't promise uninterrupted service. We're not liable for user disputes, technical issues, or losses resulting from using the platform.</p>
      <p>Our total liability is limited to €100 or the amount you paid for Markado services in the past year, whichever is higher.</p>
    `,
    },
    {
      id: 'disputes',
      title: 'Resolving Problems',
      content: `
      <p>If you have a dispute with another user, please try resolving it directly first. If that fails, contact us for help - we can review messages and mediate when possible.</p>
      <p>Serious or illegal matters should be reported to local authorities. Legal disputes with Markado are governed by North Macedonian law and handled by courts in North Macedonia.</p>
    `,
    },
    {
      id: 'termination',
      title: 'Account Closure',
      content: `
      <p>You may delete your account anytime by contacting us. Once deleted, your listings and data are removed in line with our <a href="/privacy-policy">Privacy Policy</a>.</p>
      <p>We may suspend or terminate accounts that break our rules, engage in fraud, or post illegal content. We'll usually notify you before taking action unless immediate removal is needed for safety or legal reasons.</p>
    `,
    },
    {
      id: 'changes',
      title: 'Changes to These Terms',
      content: `
      <p>We may update these Terms occasionally to reflect platform improvements or legal changes. Major updates will be announced via email or a notice on our site. Continued use of Markado means you accept the latest version.</p>
      <p>Effective Date: <strong>October 30, 2025</strong></p>
    `,
    },
  ];

  constructor() {}

  ngOnInit(): void {
    this.activeSection = this.sections[0]?.id || '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.policyContent && this.policyContent.nativeElement) {
        this.policyContent.nativeElement.classList.add('loaded');
      }
      this.setupIntersectionObserver();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {}

  private setupIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.3,
    };

    this.observer = new IntersectionObserver((entries) => {
      if (this.isScrolling) return;

      const visibleEntries = entries.filter((entry) => entry.isIntersecting);

      if (visibleEntries.length > 0) {
        const firstVisible = visibleEntries[0];
        const sectionId = firstVisible.target.id;

        if (sectionId && this.activeSection !== sectionId) {
          this.activeSection = sectionId;
        }
      }
    }, options);

    this.sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        this.observer.observe(element);
      }
    });
  }

  scrollToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      this.isScrolling = true;
      this.activeSection = sectionId;

      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });

      setTimeout(() => {
        this.isScrolling = false;
      }, 1000);
    }
  }

  isActive(sectionId: string): boolean {
    return this.activeSection === sectionId;
  }

  trackBySection(index: number, section: any): string {
    return section.id;
  }
}
